import { useEffect, useState } from "react";
import ConcentricLoader from "../Loader/ConcentricLoader";
import PaymentMethods from "./PaymentMethods";
import AdyenCheckout from "@adyen/adyen-web";
import { useValpayConfigQuery } from "../../lib/graphql/api";

interface ValpayProps {
  buttonLabel?: string;
  buttonSubmitting?: boolean;
  className?: string;
  onDelete?: (id: string) => Promise<boolean>;
  onSubmit: () => void;
  processorId: number;
  profileMode?: boolean;
  shopperReference?: string;
}

function Valpay({
  buttonLabel,
  buttonSubmitting = false,
  className,
  onDelete,
  onSubmit,
  processorId,
  shopperReference,
  profileMode = false,
}: ValpayProps): JSX.Element | null {
  const [valpayObj, setValpayObj] = useState();
  const { data, loading, error } = useValpayConfigQuery({
    variables: { processorId, shopperReference },
  });

  useEffect(() => {
    if (!buttonSubmitting && valpayObj) {
      valpayObj.setStatus("ready");
    }
  }, [buttonSubmitting]);

  const valpaySession = data ? data.valpayConfig : undefined;
  useEffect(() => {
    if (valpaySession && valpaySession.error) {
      console.error(valpaySession.error);
      return;
    }

    // Only initiate ValPay if we are ready to do so.
    if (!valpaySession) return;

    async function initializeValpay() {
      let translations = {};
      if (buttonLabel) {
        translations = {
          en_US: {
            payButton: buttonLabel,
          },
        };
      }

      const configuration = {
        environment: valpaySession.environment,
        clientKey: valpaySession.clientKey,
        paymentMethodsResponse: JSON.parse(valpaySession.paymentMethods),
        paymentMethodsConfiguration: {
          card: {
            styles: {
              base: {
                fontFamily:
                  "Nunito Sans, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue",
                fontSize: "14px",
                lineHeight: "21px",
                outline: "2px solid transparent !important",
                outlineOffset: "2px !important",
              },
              error: {
                outline: "2px solid transparent !important",
                outlineOffset: "2px !important",
              },
              placeholder: {
                color: "rgb(107 114 128)",
                opacity: "0.9",
              },
            },
          },
          storedCard: {
            showPayButton: !profileMode,
            hideCVC: profileMode,
          },
        },
        translations,
        onError: (error, component) => {
          console.error(
            "PAYMENT ERROR",
            error.name,
            error.message,
            error.stack,
            component,
          );
          component.setStatus("ready");
        },
        onSubmit: (state, component) => {
          onSubmit({
            name: state.data.paymentMethod.holderName,
            token: state.data.paymentMethod.storedPaymentMethodId,
            encryptedCardNumber: state.data.paymentMethod.encryptedCardNumber,
            encryptedExpiryMonth: state.data.paymentMethod.encryptedExpiryMonth,
            encryptedExpiryYear: state.data.paymentMethod.encryptedExpiryYear,
            encryptedSecurityCode:
              state.data.paymentMethod.encryptedSecurityCode,
            postalCode: state.data.billingAddress?.postalCode,
          });
        },
      };

      const checkout = await new AdyenCheckout(configuration);

      const dropinConfiguration = {
        onDisableStoredPaymentMethod: async (
          storedPaymentMethodId,
          resolve,
          reject,
        ) => {
          if (!onDelete) return;
          const result = await onDelete(storedPaymentMethodId);
          result ? resolve() : reject();
        },
        showRemovePaymentMethodButton: profileMode,
      };

      const dropin = checkout.create("dropin", dropinConfiguration);
      setValpayObj(dropin);
    }
    initializeValpay();
  }, [valpaySession]);

  if (error) {
    console.error(error);
    return null;
  }

  return loading ? (
    <ConcentricLoader className="opacity-60 max-w-2xl mx-auto py-10 px-4 sm:py-10 sm:px-6 lg:px-8" />
  ) : (
    <PaymentMethods className={className} valpayObj={valpayObj} />
  );
}

export default Valpay;
